import React, { useEffect, useState } from 'react'
import { toast, ToastContainer } from 'react-toastify'
import Request from '../../../helpers/request'
import * as url from '../../../helpers/url_helper'

interface RecycleBinUser {
    _id: string
    name: string
    email: string
    mobile: string
}

interface RecycleBinEntry {
    id: string
    resourceId: string
    deletedBy: RecycleBinUser | null
    deletedAt: string
    expiresAt: string
    daysRemaining: number
    meta: {
        placeName: string
        roomCount: number
        userCount: number
    }
}

const TH = ({ children }: { children: React.ReactNode }) => (
    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 13, color: '#858D9D', fontWeight: 500, background: '#F9FAFB' }}>
        {children}
    </th>
)

const TD = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
    <td style={{ padding: '12px 16px', fontSize: 14, color: '#344054', borderTop: '1px solid #E4E7EC', ...style }}>
        {children}
    </td>
)

const formatDate = (iso: string) => {
    try {
        return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
    } catch { return iso }
}

const RecycleBin: React.FC = () => {
    const [entries, setEntries] = useState<RecycleBinEntry[]>([])
    const [loading, setLoading] = useState(false)
    const [busyId, setBusyId] = useState<string | null>(null)
    const [purgeTarget, setPurgeTarget] = useState<RecycleBinEntry | null>(null)
    const [purgeInput, setPurgeInput] = useState('')

    const fetchList = () => {
        setLoading(true)
        Request.get(url.RECYCLE_BIN_LIST)
            .then((res: any) => setEntries(res?.data?.data || []))
            .catch((err: any) => toast.error(err?.response?.data?.msg || 'Failed to load recycle bin'))
            .finally(() => setLoading(false))
    }

    useEffect(() => { fetchList() }, [])

    const restore = (entry: RecycleBinEntry) => {
        const label = entry.meta?.placeName || entry.resourceId
        const summary = `${entry.meta?.roomCount ?? 0} rooms, ${entry.meta?.userCount ?? 0} users`
        if (!window.confirm(`Restore "${label}" (${summary})?\n\nIt will be moved back to live data.`)) return
        setBusyId(entry.id)
        Request.post(url.RECYCLE_BIN_RESTORE, { recycleBinId: entry.id })
            .then((res: any) => {
                toast.success(res?.data?.msg || 'Place restored')
                setEntries(prev => prev.filter(e => e.id !== entry.id))
            })
            .catch((err: any) => {
                const status = err?.response?.status
                if (status === 404) {
                    toast.error('Entry no longer available (expired or already restored elsewhere)')
                    fetchList()
                } else if (status === 409) {
                    toast.error('Cannot restore — a place with the same id already exists. Contact backend.')
                } else {
                    toast.error(err?.response?.data?.msg || 'Restore failed')
                }
            })
            .finally(() => setBusyId(null))
    }

    const confirmPurge = () => {
        if (!purgeTarget) return
        setBusyId(purgeTarget.id)
        Request.post(url.RECYCLE_BIN_PURGE, { recycleBinId: purgeTarget.id })
            .then((res: any) => {
                toast.success(res?.data?.msg || 'Recycle bin entry permanently deleted')
                setEntries(prev => prev.filter(e => e.id !== purgeTarget.id))
                setPurgeTarget(null)
                setPurgeInput('')
            })
            .catch((err: any) => {
                if (err?.response?.status === 404) {
                    toast.error('Entry no longer available')
                    fetchList()
                } else {
                    toast.error(err?.response?.data?.msg || 'Purge failed')
                }
            })
            .finally(() => setBusyId(null))
    }

    return (
        <div className="page-content">
            <ToastContainer />
            <div style={{ padding: 24 }}>
                <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>🗑️ Recycle Bin</h3>
                        <p style={{ marginTop: 4, color: '#6B7280', fontSize: 13 }}>
                            Soft-deleted places — restorable for 30 days, then auto-purged.
                        </p>
                    </div>
                    <button
                        onClick={fetchList}
                        disabled={loading}
                        style={{ padding: '6px 14px', fontSize: 13, background: '#fff', color: '#374151', border: '1px solid #D1D5DB', borderRadius: 6, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                        {loading ? 'Refreshing…' : 'Refresh'}
                    </button>
                </div>

                <div style={{ background: '#fff', border: '1px solid #E4E7EC', borderRadius: 8, overflow: 'hidden' }}>
                    {loading ? (
                        <div style={{ padding: 32, textAlign: 'center', color: '#858D9D', fontSize: 14 }}>Loading…</div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <TH>Place Name</TH>
                                    <TH>Rooms / Users</TH>
                                    <TH>Deleted By</TH>
                                    <TH>Deleted At</TH>
                                    <TH>Days Remaining</TH>
                                    <TH>Actions</TH>
                                </tr>
                            </thead>
                            <tbody>
                                {entries.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} style={{ padding: 32, textAlign: 'center', color: '#858D9D', fontSize: 14 }}>
                                            Recycle bin is empty
                                        </td>
                                    </tr>
                                ) : (
                                    entries.map(entry => {
                                        const isExpiring = entry.daysRemaining <= 3
                                        return (
                                            <tr key={entry.id}>
                                                <TD>
                                                    <strong>{entry.meta?.placeName || '--'}</strong>
                                                    <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                                                        <code>{entry.resourceId}</code>
                                                    </div>
                                                </TD>
                                                <TD style={{ fontSize: 13, color: '#475467' }}>
                                                    {entry.meta?.roomCount ?? 0} rooms · {entry.meta?.userCount ?? 0} users
                                                </TD>
                                                <TD>
                                                    {entry.deletedBy ? (
                                                        <>
                                                            <div>{entry.deletedBy.name || '--'}</div>
                                                            <div style={{ fontSize: 12, color: '#6B7280' }}>{entry.deletedBy.email}</div>
                                                        </>
                                                    ) : (
                                                        <span style={{ fontStyle: 'italic', color: '#858D9D' }}>User no longer exists</span>
                                                    )}
                                                </TD>
                                                <TD style={{ fontSize: 13 }}>{formatDate(entry.deletedAt)}</TD>
                                                <TD>
                                                    <span style={{
                                                        fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 12,
                                                        background: isExpiring ? '#FEF2F2' : '#F0FDF4',
                                                        color: isExpiring ? '#991B1B' : '#166534',
                                                        border: '1px solid ' + (isExpiring ? '#FCA5A5' : '#86EFAC'),
                                                    }}>
                                                        {entry.daysRemaining} day{entry.daysRemaining === 1 ? '' : 's'}
                                                    </span>
                                                </TD>
                                                <TD>
                                                    <button
                                                        data-testid={`restore-${entry.id}`}
                                                        onClick={() => restore(entry)}
                                                        disabled={busyId === entry.id}
                                                        style={{
                                                            padding: '4px 10px', fontSize: 12, fontWeight: 500,
                                                            background: '#fff', color: '#16A34A', border: '1px solid #16A34A',
                                                            borderRadius: 4, cursor: busyId === entry.id ? 'not-allowed' : 'pointer',
                                                            marginRight: 4, opacity: busyId === entry.id ? 0.5 : 1,
                                                        }}
                                                    >
                                                        Restore
                                                    </button>
                                                    <button
                                                        data-testid={`purge-${entry.id}`}
                                                        onClick={() => { setPurgeTarget(entry); setPurgeInput('') }}
                                                        disabled={busyId === entry.id}
                                                        title="Permanently delete (cannot be undone)"
                                                        style={{
                                                            padding: '4px 10px', fontSize: 12, fontWeight: 500,
                                                            background: '#fff', color: '#B91C1C', border: '1px solid #B91C1C',
                                                            borderRadius: 4, cursor: busyId === entry.id ? 'not-allowed' : 'pointer',
                                                            opacity: busyId === entry.id ? 0.5 : 1,
                                                        }}
                                                    >
                                                        Delete Forever
                                                    </button>
                                                </TD>
                                            </tr>
                                        )
                                    })
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {purgeTarget && (
                <div
                    data-testid="purge-dialog"
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
                    }}
                    onClick={() => { setPurgeTarget(null); setPurgeInput('') }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: '#fff', borderRadius: 12, padding: 24,
                            maxWidth: 520, width: '90%', boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                        }}
                    >
                        <h4 style={{ margin: 0, color: '#B91C1C', fontSize: 18, fontWeight: 700 }}>
                            ⚠ Permanently delete "{purgeTarget.meta?.placeName}"?
                        </h4>
                        <p style={{ marginTop: 12, fontSize: 14, color: '#344054', lineHeight: 1.5 }}>
                            This cannot be undone. The snapshot ({purgeTarget.meta?.roomCount ?? 0} rooms, {purgeTarget.meta?.userCount ?? 0} users) will be gone for good.
                        </p>
                        <p style={{ marginTop: 6, fontSize: 13, color: '#6B7280' }}>
                            The 30-day auto-cleanup would handle this on {formatDate(purgeTarget.expiresAt)} if you'd rather wait.
                        </p>
                        <p style={{ marginTop: 12, fontSize: 13, color: '#475467' }}>
                            To confirm, type the place name exactly: <code style={{ background: '#F3F4F6', padding: '2px 6px', borderRadius: 4 }}>{purgeTarget.meta?.placeName}</code>
                        </p>
                        <input
                            data-testid="purge-name-input"
                            type="text"
                            value={purgeInput}
                            onChange={e => setPurgeInput(e.target.value)}
                            placeholder={purgeTarget.meta?.placeName}
                            style={{
                                width: '100%', padding: '8px 12px', fontSize: 14,
                                border: '1px solid #D1D5DB', borderRadius: 6, marginTop: 8,
                            }}
                        />
                        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button
                                onClick={() => { setPurgeTarget(null); setPurgeInput('') }}
                                style={{ padding: '8px 16px', fontSize: 14, background: '#fff', color: '#374151', border: '1px solid #D1D5DB', borderRadius: 6, cursor: 'pointer' }}
                            >
                                Cancel
                            </button>
                            <button
                                data-testid="purge-confirm-btn"
                                onClick={confirmPurge}
                                disabled={purgeInput.trim() !== (purgeTarget.meta?.placeName || '') || busyId === purgeTarget.id}
                                style={{
                                    padding: '8px 16px', fontSize: 14, fontWeight: 500,
                                    background: purgeInput.trim() === (purgeTarget.meta?.placeName || '') ? '#B91C1C' : '#9CA3AF',
                                    color: '#fff', border: 'none', borderRadius: 6,
                                    cursor: purgeInput.trim() === (purgeTarget.meta?.placeName || '') ? 'pointer' : 'not-allowed',
                                }}
                            >
                                {busyId === purgeTarget.id ? 'Deleting…' : 'Delete Forever'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default RecycleBin
